import { useMemo, useState } from "react";
import DataTable, { Column } from "@/components/ui/DataTable";
import { ButtonAdd, Button } from "@/components/ui/button";
import { Tags } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AsyncCombobox, {
  ComboboxOption,
} from "@/components/inventory/AsyncCombobox";
import { useModal, usePagination, useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { displayPrice } from "@/lib/utils";
import {
  getExpenseCategoryLabel,
  getPaymentMethodLabel,
} from "@/lib/expenses";
import {
  useGetExpensesQuery,
  useGetExpenseCategoriesQuery,
  GetExpensesParams,
} from "@/redux/services/expense/expense.api";
import { Expense } from "@/redux/services/expense/expense.type";
import ExpenseFormModal from "./ExpenseFormModal";
import ExpenseCategoriesModal from "./ExpenseCategoriesModal";
import ExpenseDetailsDrawer from "./ExpenseDetailsDrawer";

interface ExpensesListProps {
  restaurantId: string;
}

const STATUS_OPTIONS = [
  { value: "all", label: "Tous les statuts" },
  { value: "recorded", label: "Enregistrées" },
  { value: "voided", label: "Annulées" },
];

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(iso));

const ExpensesList = ({ restaurantId }: ExpensesListProps) => {
  const { page, limit, onChange } = usePagination(25);
  const currency = useRestaurantCurrency();
  const { can } = usePermissions();

  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [categorySearch, setCategorySearch] = useState("");
  const [detailExpenseId, setDetailExpenseId] = useState<string | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const { data: categories = [] } = useGetExpenseCategoriesQuery({
    restaurantId,
  });

  // Searchable filter options. Archived categories stay selectable (to review
  // their past expenses) but are flagged so it's clear they're removed.
  const categoryFilterOptions: ComboboxOption[] = useMemo(
    () => [
      { value: "all", label: "Toutes les catégories" },
      ...categories.map((c) => ({
        value: c.id,
        label: getExpenseCategoryLabel(c),
        sublabel: c.archived ? "Supprimée" : undefined,
      })),
    ],
    [categories],
  );
  const filteredCategoryFilterOptions = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    return q
      ? categoryFilterOptions.filter((o) => o.label.toLowerCase().includes(q))
      : categoryFilterOptions;
  }, [categoryFilterOptions, categorySearch]);
  const categoryFilterLabel = categoryFilterOptions.find(
    (o) => o.value === categoryFilter,
  )?.label;

  const query: GetExpensesParams = {
    page,
    limit,
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(categoryFilter !== "all" ? { "category.uuid": categoryFilter } : {}),
  };

  const { data, isFetching } = useGetExpensesQuery({ restaurantId, query });

  const { isOpen, closeModal, openModal } = useModal<Expense>();

  const columns: Column[] = [
    {
      key: "incurredAt",
      label: "Date",
      formatter: (_v, row: Expense) => formatDate(row.incurredAt),
    },
    {
      key: "category",
      label: "Catégorie",
      formatter: (_v, row: Expense) => getExpenseCategoryLabel(row.category),
    },
    {
      key: "payee",
      label: "Bénéficiaire",
      formatter: (_v, row: Expense) => row.payee ?? "—",
    },
    {
      key: "amount",
      label: "Montant",
      formatter: (_v, row: Expense) => (
        <span className="font-medium text-gray-900">
          {displayPrice(row.amount, currency)}
        </span>
      ),
    },
    {
      key: "paymentMethod",
      label: "Paiement",
      formatter: (_v, row: Expense) => getPaymentMethodLabel(row.paymentMethod),
    },
    {
      key: "status",
      label: "Statut",
      formatter: (_v, row: Expense) =>
        row.status === "voided" ? (
          <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            Annulée
          </span>
        ) : (
          <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
            Enregistrée
          </span>
        ),
    },
  ];

  return (
    <>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            <div className="w-44">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-52">
              <AsyncCombobox
                value={categoryFilter}
                selectedLabel={categoryFilterLabel}
                onChange={setCategoryFilter}
                options={filteredCategoryFilterOptions}
                onSearchChange={setCategorySearch}
                placeholder="Catégorie"
                searchPlaceholder="Rechercher une catégorie..."
                emptyMessage="Aucune catégorie"
              />
            </div>
          </div>

          <div className="flex gap-2">
            {can("expenses.manage_categories") && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setCategoriesOpen(true)}
              >
                <Tags className="h-4 w-4" />
                Catégories
              </Button>
            )}
            {can("expenses.create") && (
              <ButtonAdd
                label="Enregistrer une dépense"
                type="button"
                onClick={openModal}
              />
            )}
          </div>
        </div>

        <DataTable
          data={data?.results ?? []}
          columns={columns}
          loading={isFetching}
          totalCount={data?.count ?? 0}
          currentPage={page}
          pageSize={limit}
          onPageChange={(p) => onChange(limit, p)}
          onPageSizeChange={(size) => onChange(size, 1)}
          onRowClick={(row: Expense) => setDetailExpenseId(row.id)}
          emptyMessage="Aucune dépense"
        />
      </div>

      {isOpen && (
        <ExpenseFormModal
          isOpen={isOpen}
          onClose={closeModal}
          restaurantId={restaurantId}
        />
      )}

      <ExpenseDetailsDrawer
        isOpen={!!detailExpenseId}
        onClose={() => setDetailExpenseId(null)}
        expenseId={detailExpenseId}
      />

      {categoriesOpen && (
        <ExpenseCategoriesModal
          isOpen={categoriesOpen}
          onClose={() => setCategoriesOpen(false)}
          restaurantId={restaurantId}
        />
      )}
    </>
  );
};

export default ExpensesList;
